import { selectDetailMenuSite, selectMeta } from '@/redux/meta/selector'
import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import { useModal } from '@/contexts/ModalContext'
import { MODAL_TYPES } from '@/libs/config/modal'
import { useRouter } from '@/libs/tolgee/navigation'
import {
  useLazyGetStoreOrderMetadataQuery,
  useLazyRetrieveCustomerInfoQuery,
  useSetPrepareCheckoutMutation,
} from '@/services/local'
import {
  formatFullDate as formatFullDateTool,
  formatShortDate as formatShortDateTool,
} from '@/utils/date'
import { formatMoney as formatMoneyTool } from '@/utils/string'
import { useTranslate } from '@tolgee/react'
import { useParams, useSearchParams } from 'next/navigation'
import useTracker from '@/hooks/useTracker'
import { decodeToken } from '@/utils/jwt'

const useAppContext = () => {
  const { t } = useTranslate()
  const router = useRouter()
  const params = useParams()
  const searchParams = useSearchParams()

  const { currencySymbol, currencyCode, decimalFormat, imagePath, logoImage } =
    useSelector(selectDetailMenuSite) || {}
  const [prepareCheckoutMutation] = useSetPrepareCheckoutMutation()
  const detailMenuSite = useSelector(selectDetailMenuSite)
  const metaState = useSelector(selectMeta)
  const { openModal } = useModal()
  const [retrieveCustomerInfo] = useLazyRetrieveCustomerInfoQuery()
  const [getStoreOrderMetadata] = useLazyGetStoreOrderMetadataQuery()
  const { segmentTrackerCheckoutStarted } = useTracker()

  const formatMoney = useCallback(
    (price?: number | null) => {
      if (!price) return ''
      return formatMoneyTool(
        price,
        currencySymbol || currencyCode,
        decimalFormat
      )
    },
    [currencyCode, currencySymbol, decimalFormat]
  )

  const formatFullDate = useCallback(
    (date: string | Date | null) => {
      if (!date) return ''
      return formatFullDateTool({ t, date, locale: params?.locale as string })
    },
    [params?.locale, t]
  )

  const formatShortDate = useCallback(
    (date: string | Date) =>
      formatShortDateTool({ t, date, locale: params?.locale as string }),
    [params?.locale, t]
  )

  const prepareToCheckout = useCallback(
    async ({
      onReadyToCheckout,
      onNotReadyToCheckout,
      onShouldRefresh,
    }: {
      onReadyToCheckout?: (storeOrder: Oms.StoreOrder) => void
      onNotReadyToCheckout?: (errorCode: string) => void
      onShouldRefresh?: () => void
    }) => {
      const checkoutResponse = await prepareCheckoutMutation(
        detailMenuSite?.storeOrderId as string
      )

      const errorCode = checkoutResponse?.data?.data?.payload?.errorCodes?.[0]

      if (errorCode === 'store_order_not_found') {
        openModal(MODAL_TYPES.REFRESH_BROWSER)
        if (typeof onShouldRefresh === 'function') {
          onShouldRefresh()
        }
        return
      }

      if (errorCode && typeof onNotReadyToCheckout === 'function') {
        onNotReadyToCheckout(
          checkoutResponse?.data?.data?.payload?.errors?.[0] || errorCode
        )
        return
      }

      if (
        checkoutResponse?.data?.data?.payload?.storeOrder &&
        checkoutResponse?.data?.data?.payload?.result !== false
      ) {
        if (typeof onReadyToCheckout === 'function') {
          onReadyToCheckout(checkoutResponse.data.data.payload.storeOrder)
        }
        return
      }
    },
    [detailMenuSite?.storeOrderId, openModal, prepareCheckoutMutation]
  )

  const redirectToCheckout = useCallback(
    async (storeOrderId: string) => {
      if (!detailMenuSite) return
      const { menu, features } = detailMenuSite
      const { enableNewCheckoutFlow } = features || {}
      const parsedToken = decodeToken(
        window.localStorage.getItem('accessToken')
      )
      const userInfo = await retrieveCustomerInfo({
        cartName: metaState?.cartName,
        clientId:
          parsedToken.clientId &&
          parsedToken.clientId !== process.env.NEXT_PUBLIC_ODDLE_PASS_CLIENT_ID
            ? parsedToken.clientId
            : undefined,
      })
      // if (
      //   userInfo?.data?.errors?.[0]?.errorCode ===
      //   'customer_require_verification'
      // ) {
      //   await window.opw.verificationAccount(menu?.id, {
      //     path: `/[locale]/checkout/[storeOrderId]`,
      //     as: `/${
      //       params?.locale
      //     }/checkout/${storeOrderId}${searchParams?.toString()}`,
      //     redirectToCheckoutPage: true,
      //   })
      //   return
      // }

      // const preservedQuery = Array.from(searchParams?.entries() || [])
      //   .filter(([key]) => key !== 'selectDiningPreference')
      //   .map(([key, value]) => `${key}=${value}`)
      //   .join('&')
      //
      // router.push(
      //   `/checkout/${storeOrderId}${enableNewCheckoutFlow ? '/address' : ''}${
      //     preservedQuery.startsWith('?') ? preservedQuery : `?${preservedQuery}`
      //   }`
      // )

      const preservedQuery = Array.from(searchParams?.entries() || [])
        .filter(([key]) => key !== 'selectDiningPreference')
        .map(([key, value]) => `${key}=${value}`)
        .join('&')
      const query =
        preservedQuery.length > 1
          ? `${preservedQuery}${
              enableNewCheckoutFlow ? '&step_address=true' : ''
            }`
          : `${enableNewCheckoutFlow ? '?step_address=true' : ''}`

      if (userInfo?.data?.data?.errorCode === 'customer_require_verification') {
        await window.opw.verificationAccount(menu?.id, {
          path: `/checkout/${storeOrderId}${
            query.startsWith('?') ? query : `?${query}`
          }`,
          redirectToCheckoutPage: true,
        })
        return
      }

      if (detailMenuSite?.customerSegmentEnabled) {
        const { data: storeOrderMetaData } = await getStoreOrderMetadata(
          storeOrderId
        )
        if (storeOrderMetaData?.data?.data?.segment) {
          segmentTrackerCheckoutStarted(storeOrderMetaData?.data?.data?.segment)
        }
      }

      router.push(
        `/checkout/${storeOrderId}${
          query.startsWith('?') ? query : `?${query}`
        }`
      )
    },
    [detailMenuSite, params?.locale, retrieveCustomerInfo, router, searchParams]
  )

  const getLogoImagePath = useCallback(
    () => `${imagePath}logo/${logoImage}.jpg`,
    [imagePath, logoImage]
  )

  return {
    formatMoney,
    formatFullDate,
    formatShortDate,
    prepareToCheckout,
    redirectToCheckout,
    getLogoImagePath,
  }
}

export default useAppContext
